import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { DocumentPreviewDialog } from '@/components/shared/documents/DocumentPreviewDialog'
import { useImageUrlRefresh } from '@/hooks/useImageUrlRefresh'
import { photoApi } from '@/lib/api/photo.api'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { hasMentions, mentionToHtml } from '@/lib/utils/mention-utils'
import {
  extractS3KeysFromHtml,
  prepareRichTextContent,
  sanitizeHtml,
} from '@/lib/utils/rich-text-utils'
import {
  isExternalUrl,
  openExternal,
  openInternalPath,
} from '@/lib/utils/tauri'

import { DocumentTypeEnum } from '@scrambl/shared'

// Import styles for text highlights and other rich text elements
import '@/components/notes/note-blocks.css'

const logger = createLogger('components/shared/RichTextDisplay')

interface RichTextDisplayProps {
  content: string
  className?: string
  enableMentionInteraction?: boolean
  onMentionClick?: (userId: string) => void
  showAvatars?: boolean
  onClick?: () => void
  placeholder?: string
  showPlaceholder?: boolean
}

export function RichTextDisplay({
  content,
  className,
  enableMentionInteraction = false,
  onMentionClick,
  showAvatars = false,
  onClick,
  placeholder = 'No content yet...',
  showPlaceholder = true,
}: RichTextDisplayProps) {
  const [processedContent, setProcessedContent] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [previewAttachment, setPreviewAttachment] = useState<{
    href: string
    filename: string
  } | null>(null)
  const displayRef = useRef<HTMLDivElement>(null)

  // Image URL refresh functionality
  const { addErrorListeners } = useImageUrlRefresh()

  // Add error event listeners to images in the display
  useEffect(() => {
    if (!displayRef.current) return

    const cleanup = addErrorListeners(displayRef.current)

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (!displayRef.current) return

      // Re-initialize checkbox states for display mode
      const checkboxes = displayRef.current.querySelectorAll(
        'input[type="checkbox"].checklist-checkbox',
      )

      checkboxes.forEach(checkbox => {
        const input = checkbox as HTMLInputElement
        const label = input.nextElementSibling as HTMLElement

        // Prevent checkbox interaction in display mode
        input.onclick = e => {
          e.preventDefault()
          return false
        }

        // Make checkbox visible
        input.style.cursor = 'pointer'
        input.style.display = 'inline-block'
        input.style.visibility = 'visible'

        if (label && label.classList.contains('checklist-label')) {
          // Make label non-editable in display mode
          label.contentEditable = 'false'

          // Apply styles based on checked state
          if (input.checked || input.hasAttribute('checked')) {
            label.style.textDecoration = 'line-through'
            label.style.opacity = '0.5'
            // Ensure checkbox appears checked
            input.checked = true
          } else {
            label.style.textDecoration = 'none'
            label.style.opacity = '1'
            input.checked = false
          }
        }
      })
    }, 50)

    return () => {
      clearTimeout(timer)
      cleanup()
    }
  }, [processedContent, addErrorListeners])

  useEffect(() => {
    // If content is empty or just contains empty paragraph tags, don't process anything
    if (
      !content ||
      content.trim() === '' ||
      content.trim() === '<p><br></p>' ||
      content.trim() === '<p></p>'
    ) {
      setProcessedContent('')
      setIsLoading(false)
      return
    }

    let cancelled = false
    setProcessedContent('')

    const processContent = async () => {
      setIsLoading(true)
      try {
        // Check if content has mentions
        const hasMentionText = hasMentions(content)

        let processedContent = content

        // Convert mentions to HTML if they exist
        if (hasMentionText) {
          processedContent = mentionToHtml(processedContent)
        }

        // Check if content contains S3 images and refresh URLs
        const s3Keys = extractS3KeysFromHtml(processedContent)
        if (s3Keys.length > 0) {
          processedContent = await prepareRichTextContent(
            processedContent,
            photoApi.getMultipleSignedUrls,
          )
        }

        // Process checklist items - add checkboxes if missing
        processedContent = processedContent.replace(
          /<div\s+class=["']checklist-item["'][^>]*>(.*?)<\/div>/gi,
          (match, content) => {
            // Check if there's already a checkbox
            if (
              content.includes('type="checkbox"') ||
              content.includes("type='checkbox'")
            ) {
              return match // Already has checkbox
            }

            // Extract the label
            const labelMatch = content.match(
              /<span\s+class=["']checklist-label["'][^>]*>(.*?)<\/span>/i,
            )
            if (labelMatch) {
              const fullLabel = labelMatch[0]

              // Check if label has strikethrough styling (indicates checked)
              const isChecked =
                fullLabel.includes('text-decoration') &&
                fullLabel.includes('line-through')

              // Create checkbox element
              const checkbox = `<input type="checkbox" class="checklist-checkbox" ${isChecked ? 'checked' : ''} />`

              // Return div with checkbox and label
              return `<div class="checklist-item">${checkbox}${fullLabel}</div>`
            }

            return match
          },
        )

        // Process links to add target="_blank" and rel="noopener noreferrer"
        processedContent = processedContent.replace(
          /<a\s+([^>]*href=["'][^"']+["'][^>]*)>/gi,
          (match, attributes) => {
            // Check if target already exists
            if (!/target=/i.test(attributes)) {
              attributes += ' target="_blank"'
            }
            // Check if rel already exists
            if (!/rel=/i.test(attributes)) {
              attributes += ' rel="noopener noreferrer"'
            }
            return `<a ${attributes}>`
          },
        )

        // Handle text colors - convert font tags to spans and adjust for dark mode
        const isDarkMode = document.documentElement.classList.contains('dark')

        // Allowlist for color values we'll interpolate into style attrs.
        // Defense-in-depth: even though sanitizeHtml runs last, don't let arbitrary strings into HTML.
        const isSafeCssColor = (c: string) =>
          /^#[0-9a-fA-F]{3,8}$/.test(c) ||
          /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/.test(c) ||
          /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/.test(
            c,
          ) ||
          /^[a-zA-Z]{3,20}$/.test(c) // named colors like 'red', 'transparent'

        // Replace font tags with a simpler approach - handle them one at a time
        let iterations = 0
        while (processedContent.includes('<font color=') && iterations < 20) {
          iterations++

          // Find the first font tag
          const fontMatch = processedContent.match(
            /<font\s+color=["']([^"']+)["']>/i,
          )
          if (!fontMatch) break

          const fullMatch = fontMatch[0]
          const color = fontMatch[1]
          const startIndex = processedContent.indexOf(fullMatch)

          // Find the corresponding closing tag
          let depth = 1
          let currentIndex = startIndex + fullMatch.length
          let endIndex = -1

          while (currentIndex < processedContent.length && depth > 0) {
            if (processedContent.substring(currentIndex).startsWith('<font')) {
              depth++
              currentIndex += 5
            } else if (
              processedContent.substring(currentIndex).startsWith('</font>')
            ) {
              depth--
              if (depth === 0) {
                endIndex = currentIndex
              }
              currentIndex += 7
            } else {
              currentIndex++
            }
          }

          if (endIndex !== -1) {
            const content = processedContent.substring(
              startIndex + fullMatch.length,
              endIndex,
            )

            // Map colors to dark mode variants if needed
            let finalColor = color
            if (isDarkMode) {
              const colorMap: { [key: string]: string } = {
                '#2c59c3': '#6b9cff',
                '#1ead5b': '#4ec880',
                '#f48a2f': '#ff9f4a',
                '#8b5cf6': '#a96bff',
                '#d03b3c': '#ff6b6b',
                'rgb(44, 89, 195)': 'rgb(107, 156, 255)',
                'rgb(30, 173, 91)': 'rgb(78, 200, 128)',
                'rgb(244, 138, 47)': 'rgb(255, 159, 74)',
                'rgb(139, 92, 246)': 'rgb(169, 107, 255)',
                'rgb(208, 59, 60)': 'rgb(255, 107, 107)',
              }
              finalColor = colorMap[color.toLowerCase()] || color
            }

            const replacement = isSafeCssColor(finalColor)
              ? `<span style="color: ${finalColor};">${content}</span>`
              : `<span>${content}</span>`
            processedContent =
              processedContent.substring(0, startIndex) +
              replacement +
              processedContent.substring(endIndex + 7)
          } else {
            break
          }
        }

        // Also handle existing span styles with colors in dark mode
        if (isDarkMode) {
          processedContent = processedContent.replace(
            /style="([^"]*color:\s*)([^;"]+)([^"]*)"/gi,
            (match, before, color, after) => {
              const colorMap: { [key: string]: string } = {
                '#2c59c3': '#6b9cff',
                '#1ead5b': '#4ec880',
                '#f48a2f': '#ff9f4a',
                '#8b5cf6': '#a96bff',
                '#d03b3c': '#ff6b6b',
                'rgb(44, 89, 195)': 'rgb(107, 156, 255)',
                'rgb(30, 173, 91)': 'rgb(78, 200, 128)',
                'rgb(244, 138, 47)': 'rgb(255, 159, 74)',
                'rgb(139, 92, 246)': 'rgb(169, 107, 255)',
                'rgb(208, 59, 60)': 'rgb(255, 107, 107)',
              }
              const trimmed = color.trim()
              const mapped = colorMap[trimmed.toLowerCase()] || trimmed
              const finalColor = isSafeCssColor(mapped) ? mapped : 'inherit'
              return `style="${before}${finalColor}${after}"`
            },
          )
        }

        if (!cancelled) {
          setProcessedContent(sanitizeHtml(processedContent))
        }
      } catch (error) {
        logger.error('Error processing rich text content:', error)
        // Fallback to original content with mentions processed
        if (!cancelled) {
          setProcessedContent(sanitizeHtml(mentionToHtml(content)))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    processContent()

    return () => {
      cancelled = true
    }
  }, [content, enableMentionInteraction, showAvatars])

  // Don't render anything if there's no content and placeholder is disabled
  if (
    (!content ||
      content.trim() === '' ||
      content.trim() === '<p><br></p>' ||
      content.trim() === '<p></p>') &&
    !showPlaceholder
  ) {
    return null
  }

  // Show loading state for very brief moment while processing images
  if (isLoading && !processedContent) {
    return (
      <div className={cn('prose prose-sm max-w-none opacity-50', className)}>
        <p>Loading...</p>
      </div>
    )
  }

  // Show placeholder if no content
  if (
    !content ||
    content.trim() === '' ||
    content.trim() === '<p><br></p>' ||
    content.trim() === '<p></p>'
  ) {
    return (
      <div
        className={cn(
          'py-1 text-sm text-muted-foreground',
          onClick &&
            'cursor-pointer transition-colors hover:text-muted-foreground',
          className,
        )}
        onClick={onClick}
      >
        {placeholder}
      </div>
    )
  }

  // Handle click events
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement

    // Handle checkbox clicks - prevent interaction and trigger edit mode
    if (
      target.tagName === 'INPUT' &&
      (target as HTMLInputElement).type === 'checkbox'
    ) {
      e.preventDefault()
      e.stopPropagation()

      // Prevent checkbox state change in display mode
      ;(target as HTMLInputElement).checked =
        (target as HTMLInputElement).hasAttribute('checked') ||
        (target as HTMLInputElement).getAttribute('checked') === 'checked'

      // Trigger edit mode when clicking on a checkbox
      if (onClick) {
        onClick()
      }
      return
    }

    // Clicking on checklist labels should also trigger edit mode
    if (
      target.classList.contains('checklist-label') ||
      target.closest('.checklist-label')
    ) {
      e.preventDefault()
      e.stopPropagation()

      // Trigger edit mode
      if (onClick) {
        onClick()
      }
      return
    }

    // Clicking on checklist item should trigger edit mode
    if (
      target.classList.contains('checklist-item') ||
      target.closest('.checklist-item')
    ) {
      e.preventDefault()
      e.stopPropagation()

      // Trigger edit mode
      if (onClick) {
        onClick()
      }
      return
    }

    // Handle link clicks - open properly in Tauri
    const anchor =
      target.tagName === 'A'
        ? (target as HTMLAnchorElement)
        : target.closest('a')
    if (anchor) {
      const href = anchor.getAttribute('href')
      if (href) {
        e.preventDefault()
        e.stopPropagation()

        // Handle attachment links as previews; downloads remain explicit.
        const isAttachment = anchor.getAttribute('data-attachment') === 'true'
        if (isAttachment) {
          const filename =
            anchor.getAttribute('data-filename') ||
            anchor.textContent ||
            'Attachment'
          setPreviewAttachment({ href, filename })
          return
        }

        if (isExternalUrl(href)) {
          void openExternal(href)
        } else if (
          href.startsWith('/') ||
          href.startsWith(window.location.origin)
        ) {
          // Internal app link: native window on desktop, new tab on web
          void openInternalPath(href)
        } else {
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      }
      return
    }

    // Handle mention clicks
    if (
      enableMentionInteraction &&
      onMentionClick &&
      target.classList.contains('mention')
    ) {
      e.preventDefault()
      e.stopPropagation()
      const userId = target.getAttribute('data-user-id')
      if (userId) {
        onMentionClick(userId)
      }
      return
    }

    // Handle general click (edit mode)
    if (onClick) {
      onClick()
    }
  }

  // Render with HTML for rich text content
  const safeContent = processedContent || sanitizeHtml(content)

  return (
    <>
      <div
        ref={displayRef}
        className={cn(
          'rich-text-display text-sm',
          // Click handler styles - minimal hover effect
          onClick &&
            'cursor-pointer rounded-md transition-colors hover:bg-muted/20',
          className,
        )}
        dangerouslySetInnerHTML={{ __html: safeContent }}
        onClick={handleClick}
      />
      <DocumentPreviewDialog
        document={
          previewAttachment
            ? {
                id: previewAttachment.href,
                displayName: previewAttachment.filename,
                fileName: previewAttachment.filename,
                type: DocumentTypeEnum.FILE,
              }
            : null
        }
        open={Boolean(previewAttachment)}
        onOpenChange={open => {
          if (!open) setPreviewAttachment(null)
        }}
        resolveSignedUrl={async () => ({
          url: previewAttachment?.href || '',
        })}
      />

      <style>{`
        .rich-text-display {
          font-family: inherit;
          font-size: inherit;
          line-height: 1.6;
          word-break: break-word;
          overflow-wrap: break-word;
        }
        
        .rich-text-display h1,
        .rich-text-display h2,
        .rich-text-display h3,
        .rich-text-display h4,
        .rich-text-display h5,
        .rich-text-display h6 {
          margin: 0.5em 0;
          font-weight: 600;
          color: hsl(var(--foreground));
        }
        
        .rich-text-display h1 { font-size: 1.5em; }
        .rich-text-display h2 { font-size: 1.25em; }
        .rich-text-display h3 { font-size: 1.1em; }
        
        .rich-text-display p {
          margin: 0.5em 0;
        }
        
        .rich-text-display p:first-child {
          margin-top: 0;
        }
        
        .rich-text-display p:last-child {
          margin-bottom: 0;
        }
        
        .rich-text-display ul,
        .rich-text-display ol {
          margin: 0.5em 0;
          padding-left: 2em;
        }
        
        .rich-text-display ul {
          list-style-type: disc;
        }

        .rich-text-display ul[data-type="taskList"] {
          list-style: none !important;
          padding: 0 !important;
          margin: 0.5rem 0 !important;
        }

        .rich-text-display ul[data-type="taskList"] li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          padding-left: 0 !important;
          list-style: none;
        }

        .rich-text-display ul[data-type="taskList"] li > label {
          flex-shrink: 0;
          user-select: none;
        }

        .rich-text-display ul[data-type="taskList"] li > div p {
          margin: 0 !important;
        }

        .rich-text-display ul[data-type="taskList"] li > label input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border: 2px solid hsl(var(--border));
          border-radius: 4px;
          background-color: hsl(var(--background));
          cursor: pointer;
          position: relative;
          transition: all 150ms;
          flex-shrink: 0;
          margin: 0;
        }

        .rich-text-display ul[data-type="taskList"] li > label input[type="checkbox"]:checked {
          background-color: hsl(var(--primary));
          border-color: hsl(var(--primary));
        }

        .rich-text-display ul[data-type="taskList"] li > label input[type="checkbox"]:checked::after {
          content: '';
          position: absolute;
          left: 5px;
          top: 2px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        .rich-text-display ul[data-type="taskList"] li[data-checked="true"] > div {
          text-decoration: line-through;
          color: hsl(var(--muted-foreground));
        }

        .rich-text-display ul[data-type="taskList"] li > div {
          flex: 1;
          min-width: 0;
        }

        .rich-text-display ul[data-type="taskList"] ul[data-type="taskList"] {
          margin-left: 1.5rem;
        }
        
        .rich-text-display ul ul {
          list-style-type: circle;
        }
        
        .rich-text-display ul ul ul {
          list-style-type: square;
        }
        
        .rich-text-display ul ul ul ul {
          list-style-type: disc;
        }
        
        .rich-text-display ol {
          list-style-type: decimal;
        }
        
        .rich-text-display ol ol {
          list-style-type: lower-alpha;
        }
        
        .rich-text-display ol ol ol {
          list-style-type: lower-roman;
        }
        
        .rich-text-display ol ol ol ol {
          list-style-type: decimal;
        }
        
        .rich-text-display li {
          margin: 0.25em 0;
          padding-left: 0.25em;
        }
        
        .rich-text-display ul ul,
        .rich-text-display ol ol,
        .rich-text-display ul ol,
        .rich-text-display ol ul {
          margin: 0.25em 0;
        }
        
        .rich-text-display strong,
        .rich-text-display b {
          font-weight: 600;
        }
        
        .rich-text-display em,
        .rich-text-display i {
          font-style: italic;
        }
        
        .rich-text-display strike,
        .rich-text-display del,
        .rich-text-display s {
          text-decoration: line-through;
        }
        
        .rich-text-display u {
          text-decoration: underline;
        }
        
        .rich-text-display a {
          color: hsl(var(--primary));
          text-decoration: underline;
          word-break: break-all;
          overflow-wrap: anywhere;
        }

        .rich-text-display a:hover {
          text-decoration: none;
        }
        
        .rich-text-display blockquote {
          margin: 0.5em 0;
          padding-left: 1em;
          border-left: 3px solid hsl(var(--border));
          font-style: italic;
          color: hsl(var(--muted-foreground));
        }
        
        .rich-text-display code {
          background: hsl(var(--muted));
          color: hsl(var(--foreground));
          padding: 0.125em 0.25em;
          border-radius: 0.25em;
          font-size: 0.875em;
          font-family: monospace;
        }
        
        .rich-text-display pre {
          background: hsl(var(--muted));
          color: hsl(var(--foreground));
          padding: 1em;
          border-radius: 0.375em;
          overflow-x: auto;
          margin: 0.5em 0;
          font-family: monospace;
          font-size: 0.875em;
          line-height: 1.5;
        }
        
        .rich-text-display pre code {
          background: transparent;
          padding: 0;
          font-size: inherit;
        }
        
        .rich-text-display img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5em;
          margin: 0.25em;
          display: inline-block;
          vertical-align: middle;
          border: 1px solid hsl(var(--border));
        }
        
        .rich-text-display table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.5em 0;
        }
        
        .rich-text-display th,
        .rich-text-display td {
          border: 1px solid hsl(var(--border));
          padding: 0.5em;
          text-align: left;
        }
        
        .rich-text-display th {
          background: hsl(var(--muted));
          font-weight: 600;
        }
        
        .rich-text-display .mention {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
          border-radius: 0.25em;
          padding: 0.125em 0.25em;
          font-weight: 500;
          text-decoration: none;
          display: inline-block;
        }
        
        .rich-text-display .mention:hover {
          background: hsl(var(--primary) / 0.2);
          cursor: ${enableMentionInteraction ? 'pointer' : 'default'};
        }
        
        .rich-text-display .checklist-item {
          display: flex !important;
          align-items: flex-start !important;
          gap: 8px !important;
          margin: 6px 0 !important;
          line-height: 1.5 !important;
          box-sizing: border-box !important;
        }
        
        .rich-text-display .checklist-item + .checklist-item {
          margin-top: 4px;
        }
        
        /* Prevent issues with nested elements */
        .rich-text-display blockquote .checklist-item,
        .rich-text-display .checklist-item blockquote {
          margin: 6px 0 !important;
          padding: 0 !important;
        }
        
        .rich-text-display .checklist-checkbox {
          appearance: none !important;
          -webkit-appearance: none !important;
          width: 20px !important;
          height: 20px !important;
          border: 2px solid hsl(var(--border)) !important;
          border-radius: 50% !important;
          background-color: hsl(var(--background)) !important;
          cursor: pointer !important;
          margin-top: 2px !important;
          position: relative !important;
          transition: all 150ms !important;
          flex-shrink: 0 !important;
          display: inline-block !important;
          vertical-align: middle !important;
        }
        
        .rich-text-display .checklist-item {
          cursor: pointer;
        }
        
        .rich-text-display .checklist-item:hover .checklist-checkbox {
          border-color: hsl(var(--primary) / 0.6);
        }
        
        .rich-text-display .checklist-label {
          cursor: pointer;
        }
        
        .rich-text-display .checklist-checkbox:checked,
        .rich-text-display .checklist-checkbox[checked],
        .rich-text-display input[type="checkbox"].checklist-checkbox:checked {
          background-color: hsl(var(--primary)) !important;
          border-color: hsl(var(--primary)) !important;
        }
        
        .rich-text-display .checklist-checkbox:checked::after,
        .rich-text-display .checklist-checkbox[checked]::after,
        .rich-text-display input[type="checkbox"].checklist-checkbox:checked::after {
          content: '' !important;
          position: absolute !important;
          left: 6px !important;
          top: 3px !important;
          width: 4px !important;
          height: 8px !important;
          border: solid white !important;
          border-width: 0 2px 2px 0 !important;
          transform: rotate(45deg) !important;
          display: block !important;
        }
        
        .rich-text-display .checklist-checkbox:focus-visible {
          outline: 2px solid hsl(var(--primary));
          outline-offset: 2px;
        }
        
        .rich-text-display .checklist-label {
          flex: 1;
          min-height: 1.5em;
          display: inline-block;
          vertical-align: middle;
        }
        
        /* Ensure input checkbox is visible */
        .rich-text-display input[type="checkbox"].checklist-checkbox {
          display: inline-block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        
        /* Highlight styles */
        .rich-text-display .text-highlight {
          padding: 2px 0;
          border-radius: 2px;
        }
        
        /* Ensure text colors are preserved and visible */
        .rich-text-display span[style*="color"],
        .rich-text-display font[color] {
          transition: color 0.2s ease;
        }
        
        /* Ensure inline styles take precedence */
        .rich-text-display [style*="color:"] {
          color: inherit;
        }
        
        /* Premium feel for highlights */
        .rich-text-display span[style*="background-color"],
        .rich-text-display .text-highlight {
          padding: 2px 4px;
          border-radius: 3px;
          display: inline;
        }
        
        /* Ensure underline is visible */
        .rich-text-display u {
          text-decoration: underline !important;
        }
        
        /* Ensure font colors work */
        .rich-text-display font[color] {
          display: inline;
        }
      `}</style>
    </>
  )
}
